// src/pages/StripeGuide.js
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Phone,
  User,
  CreditCard,
  CheckCircle,
  Shield,
  HelpCircle,
} from "lucide-react";

const StripeGuide = () => {
  const navigate = useNavigate();

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <h1 className="text-3xl font-bold text-blue-700 mb-2">
        How Stripe Payouts Work
      </h1>
      <p className="text-gray-600 mb-8">
        Please read this once before connecting your payout account.
      </p>

      {/* Step 1 */}
      <div className="bg-white border rounded-xl p-6 mb-6 flex gap-4">
        <Phone className="text-blue-600" size={32} />
        <div>
          <h2 className="text-xl font-semibold mb-1">
            Step 1: Phone Verification
          </h2>
          <p className="text-gray-600">
            Stripe will ask for your phone number and send you a one-time
            verification code (OTP). This is for security and fraud prevention.
          </p>
        </div>
      </div>

      {/* Step 2 */}
      <div className="bg-white border rounded-xl p-6 mb-6 flex gap-4">
        <User className="text-blue-600" size={32} />
        <div>
          <h2 className="text-xl font-semibold mb-1">
            Step 2: Personal & Job Details
          </h2>
          <p className="text-gray-600 mb-2">
            Stripe will ask for your legal name, date of birth, and home address.
            This is required by Canadian law for payouts.
          </p>
          <p className="text-gray-600">
            <strong>Job Title:</strong> You can enter <br />
            <span className="italic">
              Worker – Book Your Need <br />
              Independent Contractor – BYN <br />
              Service Provider – BYN
            </span>
          </p>
        </div>
      </div>

      {/* Step 3 */}
      <div className="bg-white border rounded-xl p-6 mb-6 flex gap-4">
        <CreditCard className="text-blue-600" size={32} />
        <div>
          <h2 className="text-xl font-semibold mb-1">
            Step 3: Add Your Bank Account
          </h2>
          <p className="text-gray-600">
            Stripe securely collects your bank details so payouts can be sent
            directly to you. Book Your Need never sees or stores this
            information.
          </p>
        </div>
      </div>

      {/* One-time setup */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6 flex gap-4">
        <CheckCircle className="text-green-600" size={32} />
        <div>
          <h2 className="text-xl font-semibold mb-1">
            One-Time Setup Only
          </h2>
          <p className="text-gray-700">
            Once your Stripe account is connected, you never need to repeat this
            process. You can withdraw earnings anytime unless you change your
            bank details.
          </p>
        </div>
      </div>

      {/* Security */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6 flex gap-4">
        <Shield className="text-blue-600" size={32} />
        <div>
          <h2 className="text-xl font-semibold mb-1">
            Your Information Is Secure
          </h2>
          <p className="text-gray-700">
            Stripe is used by companies like Uber, Amazon, and DoorDash. Your
            banking information is protected and never shared with Book Your
            Need.
          </p>
        </div>
      </div>

      {/* Help */}
      <div className="bg-gray-50 border rounded-xl p-6 mb-8 flex gap-4">
        <HelpCircle className="text-gray-600" size={32} />
        <div>
          <h2 className="text-xl font-semibold mb-1">
            Need Help?
          </h2>
          <p className="text-gray-600">
            If you’re confused at any step, send us a support message. Our team
            is available and will respond as quickly as possible.
          </p>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        <button
          onClick={() => navigate("/payments")}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold"
        >
          Connect Stripe Now
        </button>
        <button
          onClick={() => navigate("/payments")}
          className="border border-gray-300 px-6 py-3 rounded-lg font-semibold text-gray-700"
        >
          Back to Wallet
        </button>
      </div>
    </div>
  );
};

export default StripeGuide;
